/**
 * Utilities for working with script tags. Sync utils ported from
 * https://github.com/optimizely/optimizely/blob/devel/src/www/js/client/common.js
 * @module
 */

var DocumentEnv = require('../env/document');
var logger = require('../utils/logger');
var xhr = require('./xhr');

/**
 * Add a script tag to head (e.g. for JSONP)
 * @param {string} url URL of script to load
 * @param {function} [callback]
 */
exports.addScriptAsync = function(url:string, callback?:Function) {
  var head = DocumentEnv.querySelector('head');
  var scriptTag = DocumentEnv.createElement('script');
  scriptTag.type = 'text/javascript';
  // TODO(tyler): also support sync if possible (DocumentEnv.write)
  scriptTag.async = true;
  scriptTag.src = url;
  if (callback) {
    scriptTag.onload = callback;
  }
  head.insertBefore(scriptTag, head.firstChild);
  logger.debug('Asynchronously requesting ' + url);
};

/**
 * Attempt to synchronously load script via DocumentEnv.write or XHR -- note endpoint needs CORS enabled
 * for ALL domains. Note this might require use of `new Function` which is essentially eval. So lets
 * not rely on it for core features if possible
 * @param {string} url URL of script to load
 * @param {function} callback
 */
exports.addScriptSync = function (url:string, callback:Function) {

  // Attempt to use DocumentEnv.write() to load the script (compatible with all browsers for text/html doctypes)
  try {

    // Come up with a random id in case we add multiple scripts to the page asynchronously
    var id = 'optimizely_synchronous_script_' + Math.floor((Math.random() * 100000));

    // Prevent injection attacks (which prematurely close the quote)
    if (url.indexOf('"') !== -1) {
      logger.error('Blocked attempt to load unsafe script: ' + url);
      return;
    }

    // Splitting this up according to advice here http://javascript.info/tutorial/document-write
    // and here http://javascript.crockford.com/script.html
    DocumentEnv.write('<scr');
    DocumentEnv.write('ipt id="' + id + '" src="' + url + '"></scr');
    DocumentEnv.write('ipt>');

    // In Chrome (and possibly other browsers), there is a case when the document is safe to write to,
    // however DocumentEnv.write is blocked due to the invoking script being loaded asynchronously. This
    // outputs the following warning message in the console:
    //
    // "Failed to execute 'write' on 'Document': It isn't possible to write into a document from an
    // asynchronously-loaded external script unless it is explicitly opened."
    //
    // Unfortunately, this does not actually throw an error, so we have to explicitly check that the write
    // succeeded

    var scriptTag = DocumentEnv.querySelector('#' + id);
    if (!scriptTag) {
      throw new Error('DocumentEnv.write failed to append script');
    } else {
      scriptTag.onload = callback;
      scriptTag.onerror = function (e) {
        logger.warn('Failed to load script (' + url + ') synchronously:', e);
        exports.addScriptAsync(url, callback);
      };
    }

    // XHTML documents don't allow DocumentEnv.write(). Attempt to load via synchronous CORS
    // This is supported in all recent versions of Chrome, Firefox, Safari, IE 10+
  } catch (e) {
    logger.debug('DocumentEnv.write failed for ' + url + ': ' + e.message);

    if (!__CSP__) {
      var successCallback = function(req) {
        // This is basically the same as eval
        var executeScript = new Function(req.responseText); // eslint-disable-line
        executeScript();
        if (callback) {
          callback();
        }
      };

      // Fallback to XHR
      return xhr.request({
        url: url,
        async: false,
        contentType: 'text/plain',
        success: successCallback,
      })['catch'](function(err) {
        // If CORS fails, fall back to loading asynchronously
        logger.error('Failed to load ' + url + ' via synchronous XHR: ' + err.message);
        exports.addScriptAsync(url, callback);
      });
    } else {
      exports.addScriptAsync(url, callback);
    }
  }
};
